import { Link } from 'react-router-dom';
import { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'dark' | 'compact';
  showDescription?: boolean;
}

export function ComicCard({ comic, variant = 'default', showDescription = false }: ComicCardProps) {
  if (variant === 'dark') {
    return (
      <article className="group relative bg-foreground-900 rounded-lg overflow-hidden cursor-pointer h-full" data-product-shop="">
        <Link to={`/comic/${comic.id}`} className="block h-full">
          <div className="relative h-64 md:h-80 overflow-hidden">
            <img
              src={comic.coverImage}
              alt={`${comic.title} - 韩国漫画`}
              title={`${comic.title} - 妖精漫画网韩国漫画`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-900 via-foreground-900/20 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              {comic.isHot && (
                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-500 text-background-50 mb-2">
                  HOT
                </span>
              )}
              <h3 className="text-base font-semibold text-background-50 mb-1 font-heading">{comic.title}</h3>
              <p className="text-xs text-background-50/70 line-clamp-2">{comic.description}</p>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group" data-product-shop="">
        <Link to={`/comic/${comic.id}`} className="flex gap-3 cursor-pointer">
          <div className="w-20 h-28 shrink-0 rounded-lg overflow-hidden bg-background-200">
            <img
              src={comic.coverImage}
              alt={`${comic.title} - 韩国漫画`}
              title={`${comic.title} - 妖精漫画网韩国漫画`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
          <div className="flex flex-col justify-center min-w-0">
            <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading truncate">{comic.title}</h3>
            <p className="text-xs text-foreground-500 mb-1">{comic.author}</p>
            <div className="flex items-center gap-2 text-xs text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                {comic.rating}
              </span>
              <span>{comic.status}</span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group relative bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer h-full" data-product-shop="">
      <Link to={`/comic/${comic.id}`} className="block h-full">
        <div className="relative">
          <div className="aspect-[3/4] overflow-hidden bg-background-100">
            <img
              src={comic.coverImage}
              alt={`${comic.title} - 韩国漫画`}
              title={`${comic.title} - 妖精漫画网韩国漫画`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {comic.isHot && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-500 text-background-50">
                HOT
              </span>
            )}
            {comic.isNew && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-500 text-background-50">
                NEW
              </span>
            )}
          </div>
        </div>
        <div className="p-3 md:p-4">
          <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1 font-heading line-clamp-1">{comic.title}</h3>
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
            <span>{comic.author}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
              {comic.rating}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {comic.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] bg-secondary-100 text-secondary-700">
                {tag}
              </span>
            ))}
          </div>
          {showDescription && (
            <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{comic.description}</p>
          )}
          <div className="flex items-center justify-between text-[10px] text-foreground-400">
            <span>{comic.status}</span>
            <span>更新至{comic.totalChapters}话</span>
          </div>
        </div>
      </Link>
    </article>
  );
}